import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Truck, FilePdf, Image as ImageIcon, Clock, ShieldCheck, ArrowSquareOut } from "@phosphor-icons/react";

// We intentionally do NOT use REACT_APP_BACKEND_URL on this PUBLIC page. The backend
// env var points to the Emergent stage host; exposing it in share links (e.g., the
// "Open in new tab" button) leaks an `emergent.host` URL to customs officers and
// other recipients of a Kanal share link. Instead we route through the SAME origin
// the recipient is already on (the customer's custom domain, e.g.
// `https://kanal.cyrusfieldtech.com`). The production reverse proxy forwards `/api/*`
// to the backend, so this works in production. In dev/preview, the same origin also
// proxies `/api/*` to port 8001 via Kubernetes ingress.
const ORIGIN = typeof window !== "undefined" ? window.location.origin : "";
const API = `${ORIGIN}/api`;

function formatExpiry(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      weekday: "short",
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function PublicReceiptView() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true, error: null, meta: null });

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await axios.get(`${API}/public/receipts/${token}`);
        setState({ loading: false, error: null, meta: data });
      } catch (err) {
        const detail = err?.response?.data?.detail;
        const status = err?.response?.status;
        const msg =
          status === 410
            ? "This share link has expired. Ask the sender to generate a new one."
            : status === 404
              ? "This share link is invalid or has expired."
              : detail || "Couldn't load this receipt. Please try again.";
        setState({ loading: false, error: msg, meta: null });
      }
    };
    if (token) load();
  }, [token]);

  const fileUrl = `${API}/public/receipts/${token}/file`;

  return (
    <div className="min-h-screen bg-zinc-50">
      {/* Header */}
      <header className="bg-white border-b border-zinc-200">
        <div className="max-w-4xl mx-auto px-5 py-4 flex items-center gap-3">
          <div className="flex items-center gap-2.5">
            <img src="/brand/kanal-logo.svg" alt="Kanal" className="h-8 w-8" />
            <div>
              <div className="font-semibold text-base" style={{ fontFamily: "Work Sans" }}>
                Kanal
              </div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">
                Courier Platform
              </div>
            </div>
          </div>
          <div className="ml-auto text-[11px] uppercase tracking-wider text-zinc-500 flex items-center gap-1.5">
            <ShieldCheck size={14} weight="fill" className="text-emerald-600" />
            Verified share link
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-5 py-8" data-testid="public-receipt-page">
        {state.loading && (
          <div className="bg-white border border-zinc-200 rounded-md p-10 text-center text-sm text-zinc-500">
            Loading receipt…
          </div>
        )}

        {state.error && (
          <div className="bg-white border border-red-200 rounded-md p-8 text-center" data-testid="receipt-error">
            <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-3">
              <Clock size={22} className="text-red-600" weight="bold" />
            </div>
            <div className="text-base font-semibold text-zinc-900">Link unavailable</div>
            <div className="text-sm text-zinc-500 mt-2 max-w-md mx-auto">{state.error}</div>
          </div>
        )}

        {state.meta && (
          <>
            {/* Meta card */}
            <div className="bg-white border border-zinc-200 rounded-md p-5 mb-4" data-testid="receipt-meta">
              <div className="text-xs font-semibold uppercase tracking-[0.1em] text-zinc-500 mb-2">
                Proof of Purchase Receipt
              </div>
              <div className="flex items-start gap-3 flex-wrap">
                <div
                  className={`w-12 h-12 rounded-md flex items-center justify-center shrink-0 ${
                    state.meta.kind === "pdf"
                      ? "bg-red-50 text-red-600"
                      : "bg-sky-50 text-sky-600"
                  }`}
                >
                  {state.meta.kind === "pdf" ? (
                    <FilePdf size={24} weight="bold" />
                  ) : (
                    <ImageIcon size={24} weight="bold" />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-base font-semibold text-zinc-900 truncate" title={state.meta.file_name}>
                    {state.meta.file_name}
                  </div>
                  <div className="text-[12px] text-zinc-500 mt-1 flex items-center gap-3 flex-wrap">
                    {state.meta.package?.tracking_number && (
                      <span className="inline-flex items-center gap-1">
                        <Truck size={12} weight="bold" />
                        {state.meta.package.tracking_number}
                        {state.meta.package.recipient_name ? ` · ${state.meta.package.recipient_name}` : ""}
                      </span>
                    )}
                    {state.meta.company?.name && (
                      <span>Shared via {state.meta.company.name}</span>
                    )}
                  </div>
                </div>
                <a
                  href={fileUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1.5 text-[12px] font-medium text-[#002BF6] hover:underline"
                  data-testid="open-receipt-newtab"
                >
                  <ArrowSquareOut size={13} weight="bold" /> Open in new tab
                </a>
              </div>

              <div className="mt-4 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 text-[12px] text-amber-900 flex items-center gap-2">
                <Clock size={13} weight="bold" />
                <span>
                  This link expires on <b>{formatExpiry(state.meta.share_expires_at)}</b>
                </span>
              </div>
            </div>

            {/* File preview */}
            <div
              className="bg-white border border-zinc-200 rounded-md overflow-hidden"
              data-testid="receipt-file-preview"
            >
              {state.meta.kind === "image" ? (
                <img
                  src={fileUrl}
                  alt={state.meta.file_name}
                  className="w-full h-auto bg-zinc-100"
                />
              ) : (
                <object data={fileUrl} type="application/pdf" className="w-full h-[80vh]">
                  <div className="p-6 text-sm text-zinc-600">
                    PDF preview isn't supported in this browser.{" "}
                    <a href={fileUrl} target="_blank" rel="noreferrer" className="text-[#002BF6] underline">
                      Open the PDF in a new tab
                    </a>
                    .
                  </div>
                </object>
              )}
            </div>

            <div className="text-[11px] text-zinc-400 text-center mt-4">
              Shared securely via Kanal · For customs valuation purposes
            </div>
          </>
        )}
      </main>
    </div>
  );
}
